import { Dropdown, Icon, Stack, Text } from "@fluentui/react";

import styles from "./RAGMenuPanel.module.css"

import React, { useEffect } from "react";
import { useContext, useState } from "react";
import { AppStateContext } from "../../state/AppProvider";
import { RAGMenuSummary, TagOptions, TagUsageType } from "../../api";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ContainerTag } from "../../api/admin/models_admin";
import { FloatingToolTip, ToolTipKeyOf, useFloatingToolTip, FloatingToolTipController } from "../../components/common/ToolTips";

interface RAGTagKey {
  rag_index: number,
  tag_key: string|null,
}

const ragTagKeyOf: ToolTipKeyOf<RAGTagKey> = (key) => {
  let ret = `rag_index:${key.rag_index}`
  if (key.tag_key) {
    ret += `,tag_key:${key.tag_key}`
  }
  return ret
}

interface TagSelectorProps {
  controller: FloatingToolTipController<RAGTagKey>;
  ragIndex: number;
  tags: ContainerTag[];
  selectedTagKeys: string[];
  matchAll: boolean;
  useTags: TagUsageType;
  onTagSelected: (selectedTags: ContainerTag[]) => void;
  onMatchAllChanged: (value: boolean) => void;
  onUseTagsChanged: (value: TagUsageType) => void;
}

const TagSelector = (props: TagSelectorProps) => {
  const {controller, ragIndex, tags, selectedTagKeys, onTagSelected, matchAll, onMatchAllChanged, useTags, onUseTagsChanged} = props;
  const naturalCollator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base'
  });
  const isTagSelectable = useTags;
  return (<Stack tokens={{childrenGap: 16}} style={{
      maxWidth: "100%", 
      padding: "5px", 
      backgroundColor: "#f9f9f9",
      marginLeft: "30px"}}>
    <Dropdown label="サブカテゴリ絞り込み" options={[
      { key: String(false), text: "絞り込みをしない" },
      { key: String(true), text: "絞り込みをする" },
    ]} selectedKey={String(useTags)} onChange={(_, v) => {
      const key = v?.key
      if(key !== undefined) {
        onUseTagsChanged(key === String(true))
      }
    }} />
    {/*
    <Dropdown label="絞り込み方法" options={[
      { key: "or", text: "いずれかを含む" },
      { key: "and", text: "すべて含む" },
    ]} selectedKey={matchAll ? "and" : "or"} disabled={!isTagSelectable} onChange={(_, v) => {
      const key = v?.key
      if(key) {
        onMatchAllChanged(key == "and");
      }
    }} />
    */}
    <Stack tokens={{childrenGap: 0}}>
      {
        [...tags].sort((t1, t2) => naturalCollator.compare(t1.value, t2.value)).map((t) => {
          const baseClass = selectedTagKeys.includes(t.key) ? styles.tagItemSelected : styles.tagItem
          return <span key={t.key}
            data-testid={`rag-tag-${ragIndex}-${t.key}`}
            data-selected={selectedTagKeys.includes(t.key)}
            {...controller.getTargetProps({rag_index: ragIndex, tag_key: t.key})}
            className={isTagSelectable ? baseClass : `${baseClass} ${styles.tagItemDisabled}`}
            aria-disabled={!isTagSelectable}
            onClick={() => {
              if (!isTagSelectable) return;
              let newKeys: string[] = [];
              if (selectedTagKeys.includes(t.key)) {
                newKeys = selectedTagKeys.filter(v => v != t.key);
              } else {
                newKeys = [...selectedTagKeys, t.key];
              }
              onTagSelected(tags.filter(t => newKeys.includes(t.key)));
            }}
          >
            {t.value}
          </span>
        })
      }
    </Stack>
  </Stack>)
}

// キャッシュ
const ragSummaryCache = new Map<string, string>()

export function RAGMenu({  }) {
  const appStateContext = useContext(AppStateContext)
  const [, forceUpdate] = useState(0)
  const rag_id_set = appStateContext?.state.selectedRAGIds as Set<number>
  const isMobile = useIsMobile()
  const ragSummaryToolTip = useFloatingToolTip<RAGTagKey>({keyOf: ragTagKeyOf})
  const ragMenuList = appStateContext?.state.RAGMenuList;
  const documentSearchToolArg = appStateContext?.state.documentSearchToolArg
  const navigate = useNavigate()

  // 初期フェッチ
  useEffect(() => {
    const loadRagSummaries = async () => {
      if (!ragMenuList) return
      for (const rag_menu of ragMenuList){
        const key = ragTagKeyOf({rag_index: rag_menu.id, tag_key: null})
        if (ragSummaryCache.has(key)) continue

        const result = await RAGMenuSummary(rag_menu.id)

        // カテゴリのサマリテキスト
        if(result.total_count != 0){
          let summaryText = "以下のファイルが含まれています\n" + result.files.map(v => `・${v.filename}`).join("\n")
          const omittedFileCount = result.total_count - result.files.length;
          if (omittedFileCount > 0){
            summaryText += `\n他${omittedFileCount}件`
          }
          ragSummaryCache.set(key, summaryText)
        }
        else {
          ragSummaryCache.set(key, "ファイルが存在しません")
        }
        
        // タグのサマリテキスト
        const tagSummary = new Map<string, string[]>()
        for (const resultValue of result.files) {
          if (resultValue.tag_keys === null) continue;
          for (const tagKey of resultValue.tag_keys) {
            const current = tagSummary.get(tagKey)
            tagSummary.set(tagKey, [...(current ?? []), resultValue.filename])
          }
        }

        for (const tagInfo of rag_menu.tags ?? []) {
          const fileNames = tagSummary.get(tagInfo.key)
          if (fileNames !== undefined) {
            const summaryText = "以下のファイルが含まれています\n" + fileNames.map(v => `・${v}`).join("\n")
            ragSummaryCache.set(ragTagKeyOf({rag_index: rag_menu.id, tag_key: tagInfo.key}), summaryText)
          }
          else {
            ragSummaryCache.set(ragTagKeyOf({rag_index: rag_menu.id, tag_key: tagInfo.key}), "ファイルが存在しません")
          }
        }

        forceUpdate(n => n + 1)
      }
    }
    loadRagSummaries()

  }, [ragMenuList])

  const updateTagOptions = (rag_id: string|number, patch: Partial<TagOptions>) => {
    if (documentSearchToolArg === undefined) return;
    const defaultValue = {
      tag_keys: [],
      match_all: false,
      use_tags: false,
    };
    const currentTagOptions = documentSearchToolArg.tag_options.get(rag_id.toString()) ?? defaultValue
    documentSearchToolArg.tag_options.set(rag_id.toString(), { ...currentTagOptions, ...patch });
    appStateContext?.dispatch({type: "SET_DOCUMENT_SEARCH_TOOL_ARG", payload: documentSearchToolArg});
  };
  const handleTagSelected = (rag_id: string|number, selectedTags: ContainerTag[]) => {
    updateTagOptions(rag_id, { tag_keys: selectedTags.map(t => t.key) });
  };
  const handleMatchAllChanged = (rag_id: string|number, value: boolean) => {
    updateTagOptions(rag_id, { match_all: value });
  };
  const handleUseTagsChanged = (rag_id: string|number, value: TagUsageType) => {
    updateTagOptions(rag_id, { use_tags: value });
  };

  useEffect(() => {
    for (const rag_id of rag_id_set) {
      if (!documentSearchToolArg?.tag_options.has(rag_id.toString())){
        handleUseTagsChanged(rag_id, false);
      }
    }
  }, [rag_id_set])

  const handleRAGMenuClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, id: any) => {
    const id_num = Number(id)
    // ctrlを押しながらクリック、またはモバイルの場合複数選択
    if ((e.ctrlKey && !e.metaKey) || (!e.ctrlKey && e.metaKey) || isMobile) {
      if (rag_id_set.has(id_num)) {
        // 1つしか選択されていない場合は消さない
        if (1 < rag_id_set.size){
          rag_id_set.delete(id_num)
        }
      }
      else {
        rag_id_set.add(id_num)
      }
    } 
    // 通常の個別選択
    else {
      rag_id_set.clear()
      rag_id_set.add(id_num)
    }

    appStateContext?.dispatch({ type: 'SET_RAG_CHAT', payload: rag_id_set })
    navigate("/")
  };

    return (
      <section className={styles.container} data-is-scrollable aria-label={"RAG menu panel"}>
        <Text style={{ fontSize: "13px", paddingLeft: "10px", fontWeight: "600" }}>
          { isMobile ? "タップで選択・解除" : "Ctrl+左クリックで複数選択" }
        </Text>
        <div style={{overflowY: "auto"}}>
          <Stack aria-label="RAG menu content">
            {(ragMenuList ?? []).map((ragmenu) => (
              <Stack key={ragmenu.id}>
                <Stack
                  key={"rag_item_"+ragmenu.id}
                  data-testid={`rag-item-${ragmenu.id}`}
                  data-selected={rag_id_set.has(Number(ragmenu.id))}
                  {...ragSummaryToolTip.getTargetProps({rag_index: ragmenu.id, tag_key: null})}
                  verticalAlign="center"
                  className={styles.itemCell} 
                  horizontal 
                  onClick={(e) => handleRAGMenuClick(e, ragmenu.id)}
                  tokens={{childrenGap: "10px"}}
                >
                  {
                    <Icon 
                      className={styles.itemCellTextSelected} 
                      style={{
                        visibility: rag_id_set.has(Number(ragmenu.id)) ? "visible" : "hidden"
                      }}
                      iconName="Accept" 
                    />
                  }
                  <Text className={styles.itemCellText} style={{ fontSize: 16 }}>
                    {ragmenu.name}
                  </Text>
                </Stack>
                {
                  rag_id_set.has(Number(ragmenu.id)) && ragmenu.tags && ragmenu.tags.length > 0 && <TagSelector 
                    controller={ragSummaryToolTip}
                    ragIndex={ragmenu.id}
                    tags={ragmenu.tags} 
                    selectedTagKeys={documentSearchToolArg?.tag_options.get(ragmenu.id.toString())?.tag_keys ?? []}
                    onTagSelected={(tags) => {
                      handleTagSelected(ragmenu.id, tags);
                    }}
                    matchAll={documentSearchToolArg?.tag_options.get(ragmenu.id.toString())?.match_all ?? false}
                    onMatchAllChanged={(value) => {
                      handleMatchAllChanged(ragmenu.id, value);
                    }}
                    useTags={documentSearchToolArg?.tag_options.get(ragmenu.id.toString())?.use_tags ?? false}
                    onUseTagsChanged={(value) => {
                      handleUseTagsChanged(ragmenu.id, value);
                    }} />
                }
              </Stack>
            ))}
          </Stack>
        </div>
        {/* ホバーしたら出てくるやつ */}
        <FloatingToolTip controller={ragSummaryToolTip} offset={{x: 15, y:0}}>
          {(ragTagKey) => {
            const rag_id = ragTagKey.rag_index
            const tag_key = ragTagKey.tag_key
            // カテゴリそのものにホバーした場合
            if (tag_key === null) {
              const current_rag_elem = ragMenuList?.find(v => v.id === rag_id)
              if (!current_rag_elem) return null
              return (<>
                {
                  0 < current_rag_elem.prompt_examples.length &&
                  <Text style={{
                    display: "block",
                    whiteSpace: "pre",
                    fontSize: "12px",
                    textDecorationLine: "underline",
                    color: "blue",
                    cursor: "pointer"
                  }} onClick={() => window.open("/user_prompt_example/" + String(rag_id), '_blank', 'menubar=no,width=960,height=640')}>
                    <b>サンプルプロンプトはここをクリック</b>
                  </Text>
                }
                <Text style={{
                  whiteSpace: "pre",
                  fontSize: "12px"
                }}>
                  {ragSummaryCache.get(ragTagKeyOf(ragTagKey)) ?? "情報取得中"}
                </Text>
              </>)
            }
            // カテゴリ内のサブカテゴリにホバーした場合
            else {
              return (
                <Text style={{
                  whiteSpace: "pre",
                  fontSize: "12px"
                }}>
                  {ragSummaryCache.get(ragTagKeyOf(ragTagKey)) ?? "情報取得中"}
                </Text>
              )
            }
          }}
        </FloatingToolTip>
      </section>
    );
  }
